import type { Channel, LocalMessage, UserResponse } from 'stream-chat';
import { MessageInput, VirtualizedMessageList } from 'stream-chat-react';
import { CHAT_LABEL_UNKNOWN_CHAT, MESSAGE_INPUT_MAX_ROWS } from '@features/components/Chat/constants';
import { useHandleMentionUser } from './hooks/useHandleMentionUser';
import { PinnedMessagesPanel } from '../../PinnedMessagesPanel';
import { ChatDetailsPanel } from '../ChatDetailsPanel/ChatDetailsPanel';
import { PinnedMessagesBanner } from './PinnedMessagesBanner';
import { createCustomMessageRenderer } from './utils/customMessageRenderer';

type ChatMessagesContentProps = {
  channel: Channel;
  userId: string;
  pinnedMessages: LocalMessage[];
  isPinnedPanelOpen: boolean;
  isDetailsPanelOpen: boolean;
  loadingPinnedMessages: boolean;
  isReadOnly?: boolean;
  readOnlyMessage?: string;
  inlineLayout?: boolean;
  onOpenPinnedPanel: () => void;
  onClosePanels: () => void;
  onMentionUser?: (user: UserResponse) => void;
};

export const ChatMessagesContent = ({
  channel,
  userId,
  pinnedMessages,
  isPinnedPanelOpen,
  isDetailsPanelOpen,
  loadingPinnedMessages,
  isReadOnly,
  readOnlyMessage,
  inlineLayout,
  onOpenPinnedPanel,
  onClosePanels,
  onMentionUser,
}: ChatMessagesContentProps) => {
  const handleMentionUser = useHandleMentionUser(onMentionUser);

  if (isPinnedPanelOpen) {
    return (
      <PinnedMessagesPanel
        pinnedMessages={pinnedMessages}
        chatName={channel.data?.name || CHAT_LABEL_UNKNOWN_CHAT}
        loading={loadingPinnedMessages}
        onClose={onClosePanels}
      />
    );
  }

  if (isDetailsPanelOpen) {
    return (
      <ChatDetailsPanel
        channel={channel}
        onClose={onClosePanels}
        onMentionUser={handleMentionUser}
        inlineLayout={inlineLayout}
      />
    );
  }

  const customRenderer = createCustomMessageRenderer(channel, userId);

  return (
    <>
      <PinnedMessagesBanner pinnedMessages={pinnedMessages} onOpen={onOpenPinnedPanel} />
      <VirtualizedMessageList closeReactionSelectorOnClick customMessageRenderer={customRenderer} />
      {!isReadOnly && <MessageInput maxRows={MESSAGE_INPUT_MAX_ROWS} focus />}
      {isReadOnly && <p className="text-gray-700 text-center p-4">{readOnlyMessage}</p>}
    </>
  );
};
